// EmptyState — centered icon + heading + description + optional action.
//
// Uses the `recessed` surface (pale parchment-darker) per the design
// brief; the icon sits above the heading in text-tertiary, the heading
// in text-primary, the description in text-secondary, and the action is
// rendered as-is (typically a primary <Button>).

import { createElement, type ComponentType, type ReactNode } from "react";

import { cn } from "./cn";

// Use a permissive icon prop type so Lucide icons (which carry the full
// SVG attribute set + ref forwarding) slot in without TS friction. The
// component only ever passes `size` and `className`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EmptyStateIcon = ComponentType<any>;

export interface EmptyStateProps {
  icon?: EmptyStateIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg bg-recessed px-6 py-16 text-center",
        className,
      )}
    >
      {Icon &&
        createElement(Icon, { size: 32, className: "text-text-tertiary" })}
      <h3 className="text-md font-semibold text-text-primary">{title}</h3>
      {description && (
        <p className="max-w-sm text-sm text-text-secondary">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
